/**
 * 에이전트 내려받기 — **고객이 직접 여는 자리.**
 *
 * `/agent` 화면과 무엇이 다른가: 저쪽은 공급사(`system`)의 자리라 **산출물 등록과 롤아웃**이
 * 함께 있다. 롤아웃은 되돌릴 수 없고(업데이터가 다운그레이드를 거부한다) 전 화면의
 * 소프트웨어를 한 번에 바꾸는 조작이라, 기기를 늘리려는 고객사 담당자에게 그 판을 함께
 * 내밀 이유가 없다. 여기는 **받는 것만** 한다.
 *
 * 그래서 이 화면에는 없는 것이 있다 — 산출물 올리기, 채널 승격, 설치 토큰 발급. 있는 것은
 * 하나다: 기기에 깔 파일과, 깔고 나서 할 일.
 *
 * 안드로이드는 **주소 하나**로 끝난다(`/apk`). 그 주소가 설명과 내려받기 단추가 있는 화면을
 * 열고, APK 에는 서버 주소가 박혀 있어 기기 앞에서 아무것도 묻지 않는다. 윈도우는 그렇지
 * 않다 — 설치 파일만 받아 실행하면 첫 화면이 서버 주소를 묻는다. 그 사실을 감추지 않고
 * 주소를 함께 적어 둔다(감추면 현장에서 전화가 온다).
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Download, Monitor, Smartphone } from 'lucide-react';
import type { AgentRelease, Me, SignageOneClient } from '../api/client.js';
import { Badge, CommandBox, Empty, ErrorNote, formatBytes, Loading, Panel, RelTime } from '../components/ui.js';
import { QrCode } from '../components/QrCode.js';
import { HelpTip } from '../components/HelpTip.js';
import { useAction, useAsync } from '../hooks.js';
import { androidArchs, DEFAULT_APK_ARCH, resolveArch } from './AgentInstall.js';

/**
 * 고객에게 보여줄 산출물 — **지금 배포 중인 것 하나씩만.**
 *
 * 목록을 그대로 펴면 버전이 쌓인 테넌트에서 열 줄이 서고, 그중 어느 것을 받아야 하는지는
 * 아무도 모른다. 채널(stable)이 가리키는 것이 곧 "지금 이 서비스의 판" 이므로 그것을 쓰고,
 * 롤아웃을 아직 정하지 않았으면 가장 최근에 올라온 것을 쓴다.
 */
export function pickForDownload(
  releases: readonly AgentRelease[],
  stable: string | null,
  target: AgentRelease['target'],
  arch?: string,
): AgentRelease | null {
  const candidates = releases.filter(
    (release) =>
      release.target === target && release.kind === 'installer' && (!arch || release.arch === arch),
  );
  if (candidates.length === 0) return null;
  return candidates.find((release) => release.version === stable) ?? candidates[0] ?? null;
}

export function AgentDownloadScreen({
  client,
  me,
}: {
  client: SignageOneClient;
  me: Me;
}): ReactNode {
  const catalog = useAsync(() => client.agentCatalog(), [client]);
  const action = useAction();
  const [chosen, setChosen] = useState<string | null>(null);

  const releases = catalog.data?.items ?? [];
  const stable = catalog.data?.rollout.stable_version ?? null;
  const baseUrl = catalog.data?.install.base_url ?? '';
  const host = baseUrl.replace(/^https?:\/\//, '');

  const androidReleases = useMemo(
    () => releases.filter((release) => release.target === 'android' && release.kind === 'installer'),
    [releases],
  );
  const arch = resolveArch(chosen, androidReleases);
  const android = pickForDownload(releases, stable, 'android', arch);
  const windows = pickForDownload(releases, stable, 'windows');

  /* 토큰이 없어도 열리는 주소다 — 기기 앞에서 칠 수 있는 길이여야 한다. */
  const apkUrl = `${baseUrl}/apk${arch === DEFAULT_APK_ARCH ? '' : `?arch=${arch}`}`;

  return (
    <div className="screen">
      <Panel
        title="에이전트 설치"
        className="screen-fill"
        help="기기에 맞는 파일을 설치한 뒤, 화면에 뜬 6자리 코드를 [화면 ▸ 화면 추가]에 입력하면 등록됩니다."
      >
        <div className="p-4">
          {catalog.loading && !catalog.data ? <Loading /> : null}
          {catalog.error ? <ErrorNote message={catalog.error} /> : null}
          {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}

          {catalog.data && releases.length === 0 ? (
            <Empty
              title="내려받을 수 있는 설치 파일이 없습니다"
              hint="공급사가 에이전트를 등록하면 여기에 표시됩니다."
            />
          ) : null}
        </div>

        {catalog.data && releases.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 border-t border-slate-100 p-4 xl:grid-cols-2">
            {/* ── 안드로이드 ───────────────────────────────────── */}
            <section className="flex flex-col gap-3 rounded-xl border border-slate-200 p-4">
              <h3 className="flex items-center gap-2 text-[13.5px] font-semibold text-slate-800">
                <Smartphone className="h-4 w-4 text-slate-400" strokeWidth={1.9} />
                안드로이드 (TV 박스 · 태블릿)
              </h3>

              {android ? (
                <>
                  {androidArchs(androidReleases).length > 1 ? (
                    <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
                      <span className="text-slate-500">
                        기기 칩
                        <HelpTip text="대부분의 기기는 arm64-v8a 입니다. 구형 32비트 기기는 armeabi-v7a 를 고르세요." label="기기 칩 도움말" />
                      </span>
                      <div className="seg seg-sm">
                        {androidArchs(androidReleases).map((value) => (
                          <button
                            key={value}
                            type="button"
                            aria-pressed={arch === value}
                            onClick={() => setChosen(value)}
                            className="seg-btn mono"
                          >
                            {value}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                    <div className="flex flex-col items-center gap-1.5">
                      <QrCode value={apkUrl} label="APK 내려받기 QR 코드" size={132} />
                      <span className="text-[11.5px] text-slate-400">기기로 스캔</span>
                    </div>

                    <div className="flex min-w-0 flex-1 flex-col gap-2">
                      <p className="text-[12.5px] leading-relaxed text-slate-600">
                        카메라가 없으면 기기 브라우저에 아래 주소를 입력하세요.
                        <strong className="font-medium text-slate-800"> 코드·토큰은 필요 없습니다.</strong>
                      </p>
                      <p className="font-mono text-[15px] font-semibold tracking-wide text-slate-900">
                        {host}/apk
                      </p>
                      <button
                        type="button"
                        className="btn btn-primary self-start"
                        disabled={action.pending}
                        onClick={() => void action.run(() => client.downloadRelease(android), () => undefined)}
                      >
                        <Download className="h-4 w-4" strokeWidth={2} />
                        {action.pending ? '받는 중…' : 'APK 내려받기'}
                      </button>
                      <p className="text-[11.5px] text-slate-400">
                        v{android.version} · {formatBytes(android.size)} · <RelTime iso={android.published_at} />
                        {android.version === stable ? null : <Badge tone="warn">배포 중인 버전 아님</Badge>}
                      </p>
                    </div>
                  </div>

                  <ol className="ml-4 list-decimal text-[12.5px] leading-relaxed text-slate-600">
                    <li><strong>알 수 없는 출처</strong> 허용을 물으면 허용합니다.</li>
                    <li>설치 후 앱을 엽니다. 서버 주소는 입력하지 않아도 됩니다.</li>
                    <li>화면에 뜬 <strong>6자리 코드</strong>를 [화면 ▸ 화면 추가]에 입력합니다.</li>
                  </ol>
                </>
              ) : (
                <p className="hint">등록된 안드로이드 설치 파일이 없습니다.</p>
              )}
            </section>

            {/* ── 윈도우 ──────────────────────────────────────── */}
            <section className="flex flex-col gap-3 rounded-xl border border-slate-200 p-4">
              <h3 className="flex items-center gap-2 text-[13.5px] font-semibold text-slate-800">
                <Monitor className="h-4 w-4 text-slate-400" strokeWidth={1.9} />
                윈도우 PC
              </h3>

              {windows ? (
                <>
                  <button
                    type="button"
                    className="btn btn-primary self-start"
                    disabled={action.pending}
                    onClick={() => void action.run(() => client.downloadRelease(windows), () => undefined)}
                  >
                    <Download className="h-4 w-4" strokeWidth={2} />
                    {action.pending ? '받는 중…' : '설치 파일 내려받기'}
                  </button>
                  <p className="text-[11.5px] text-slate-400">
                    v{windows.version} · {formatBytes(windows.size)} · <RelTime iso={windows.published_at} />
                  </p>

                  <ol className="ml-4 list-decimal text-[12.5px] leading-relaxed text-slate-600">
                    <li>받은 파일을 사이니지 PC 로 옮겨 실행합니다.</li>
                    {/*
                      **주소를 묻는다는 사실을 숨기지 않는다.** 안드로이드와 달리 이 파일에는
                      서버 주소가 들어 있지 않다. 현장에서 그 화면을 만나면 전화가 오므로,
                      넣을 값을 여기 미리 적어 둔다.
                    */}
                    <li>
                      처음 실행하면 서버 주소를 묻습니다. 아래 주소를 입력하세요.
                      <span className="mt-1 block">
                        <CommandBox command={baseUrl} />
                      </span>
                    </li>
                    <li>화면에 뜬 <strong>6자리 코드</strong>를 [화면 ▸ 화면 추가]에 입력합니다.</li>
                  </ol>

                  <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] leading-relaxed text-amber-800">
                    <strong className="font-medium">Windows 보호 경고(SmartScreen)</strong>가 뜨면 [추가 정보] ▸
                    [실행] 을 누르세요.
                    <HelpTip
                      text="코드 서명 인증서가 아직 없어 나타나는 경고입니다. 콘솔에서 제공한 설치 파일입니다."
                      label="SmartScreen 경고 도움말"
                    />
                  </p>
                </>
              ) : (
                <p className="hint">등록된 윈도우 설치 파일이 없습니다.</p>
              )}
            </section>
          </div>
        ) : null}
      </Panel>

      {/*
        업데이트는 여기서 하지 않는다 — 기기가 스스로 받는다. 그 사실을 적어 두지 않으면
        "새 버전이 나왔는데 어디서 받나" 를 이 화면에서 찾게 된다.
      */}
      <p className="hint px-1">
        설치된 기기는 <strong>새 버전을 자동으로 받습니다</strong>. 버전이 오르지 않는 화면은 [화면] 목록의
        버전 열에서 확인해 공급사에 알려 주세요.
        {me.capabilities.includes('system') ? ' 배포(롤아웃)는 [에이전트] 화면에서 합니다.' : ''}
      </p>
    </div>
  );
}
